'use client';

import Link from '@eliadmin/core/src/components/link';

import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
import { Field } from '@eliadmin/core/src/components/form/floating-label-field/field';

const priceListSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    phone: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    text: z.string(),
    custom: z.object({
      visualization: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      number_of_floors: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      usable_area: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      land_type: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      roof_type: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    }),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    'g-recaptcha-response': z.string().default(''),
  });

export type PriceListFormValues = z.infer<ReturnType<typeof priceListSchema>>;

export function PriceListForm() {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<PriceListFormValues>({
    resolver: zodResolver(priceListSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      custom: {
        visualization: '',
        number_of_floors: '',
        usable_area: '',
        land_type: '',
        roof_type: '',
      },
      text: '',
      gdpr: false,
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: sendContactMessage,
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {
      await mutateAsync({
        data: {
          ...data,
        },
        locale,
      });
      toast({
        title: t('text658'),
        variant: 'success',
      });
      router.push(ROUTES.HOME());
    } catch (err) {
      // handle 422
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text657'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className={cn('grid w-full grid-cols-12 gap-4 @container', 'ea-contact-form')}
      >
        {[
          {
            name: 'visualization',
            label: 'Architektonická štúdia + 3D vizualizácie',
            options: ['Áno', 'Nie'],
          },
          {
            name: 'number_of_floors',
            label: 'Počet podlaží',
            options: ['1', '2', '3', '4+'],
          },
          {
            name: 'usable_area',
            label: 'Riešená úžitková plocha',
            options: ['do 100m2', 'od 100 do 150 m2', 'od 150 do 250m2'],
          },
          {
            name: 'land_type',
            label: 'Typ pozemku',
            options: ['rovinatý', 'svahovitý'],
          },
          {
            name: 'roof_type',
            label: 'Typ strechy',
            options: ['rovná', 'šikmá'],
          },
        ].map(({ name, label, options }) => (
          <FormField
            key={name}
            control={form.control}
            name={`custom.${name}` as any}
            render={({ field }) => (
              <FormItem className="col-span-12 mt-2 flex w-full flex-col items-start justify-start gap-4 sm:mt-4 lg:mt-6">
                <FormLabel className="text-xl font-light text-black xl:text-2xl">{label}</FormLabel>
                <div className="flex flex-wrap gap-4">
                  {options.map(option => (
                    <label key={option} className="flex items-center gap-2">
                      <input
                        type="radio"
                        value={option}
                        checked={field.value === option}
                        onChange={() => field.onChange(option)}
                        className={cn(
                          'min-h-10 min-w-10 text-sm font-normal',
                          'ea-price-list-form__radio',
                        )}
                      />
                      {option}
                    </label>
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        ))}
        <Field
          control={form.control}
          name="name"
          label={t('text1044')}
          required
          className={{ wrapper: 'col-span-12 mt-4 w-full sm:mt-6 lg:mt-8' }}
        />
        <Field
          control={form.control}
          name="email"
          label={t('text3599')}
          required
          className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
        />
        <Field
          control={form.control}
          name="phone"
          label={t('text1258')}
          required
          className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
        />
        <Field
          control={form.control}
          name="text"
          label="Vaša správa (stručný popis)"
          required
          className={{ wrapper: 'col-span-12 w-full' }}
        />

        <FormField
          control={form.control}
          name="gdpr"
          render={({ field }) => (
            <FormItem
              className={cn(
                'col-span-12 flex items-center gap-2',
                'ea-contact-form__gdpr-checkbox-item ea-contact-form__checkbox-item',
              )}
            >
              <FormControl>
                <Checkbox
                  ref={field.ref}
                  name={field.name}
                  disabled={field.disabled}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className={cn('', 'ea-contact-form__gdpr-checkbox ea-contact-form__checkbox')}
                />
              </FormControl>
              <FormLabel className={cn('', 'ea-contact-form__gdpr-checkbox-label')}>
                {t('text613')}{' '}
                <Link
                  href={getDefaultUrlForTosOrGdprLocalized(ROUTES?.GDPR?.() ?? '/', locale, 'gdpr')}
                  className={cn('', 'ea-contact-form__gdpr-link')}
                  prefetch={false}
                >
                  <span className="text-black">{t('text614')}</span>
                </Link>
              </FormLabel>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'g-recaptcha-response'}
          render={() => (
            <FormItem required className={cn('col-span-12')}>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="col-span-12 flex w-full items-center justify-center">
          <Button
            type="submit"
            className={cn('w-full sm:w-fit', 'ea-contact-form__submit')}
            isLoading={form.formState.isSubmitting}
          >
            {t('text1118')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
